/**
 * External dependencies
 */
import clsx from 'clsx';
import type { Maybe } from '@nab/types';

/**
 * WordPress dependencies
 */
import {
	Button,
	Dropdown,
	ToolsPanelItem,
	HStack,
	ZStack,
	DropdownContentWrapper,
	Flex,
	ColorIndicator,
	FlexItem,
} from '@safe-wordpress/components';
import { useRef } from '@safe-wordpress/element';

/**
 * Internal dependencies
 */
import { Tabs } from '../internal/wordpress/components/tabs';
import { useColorsPerOrigin, useGradientsPerOrigin } from './utils';
import {
	BackgroundImageControl,
	ColorGradientControl,
	getEditorSettings,
} from './wordpress/block-editor';

export type ColorPanelDropdownProps = {
	readonly onSelect: () => void;
	readonly onDeselect: () => void;
	readonly label: string;
	readonly hasValue: () => boolean;
	readonly isShownByDefault?: boolean;
	readonly indicators: ReadonlyArray< string >;
	readonly tabs: Readonly< [ TabProps, ...TabProps[] ] >;
	readonly disabled?: boolean;
};

export type TabProps = ColorPanelTabProps | ImagePanelTabProps;

type ColorPanelTabProps = {
	readonly type: 'color' | 'gradient';
	readonly key: string;
	readonly label: string;
	readonly userValue?: string;
	readonly inheritedValue?: string;
	readonly setValue: ( value?: string ) => void;
};

type ImagePanelTabProps = {
	readonly type: 'image';
	readonly key: string;
	readonly label: string;
	readonly userValue?: ImageValue;
	readonly inheritedValue?: ImageValue;
	readonly disabledProps: ReadonlyArray<
		| 'backgroundImage'
		| 'backgroundAttachment'
		| 'backgroundPositionX'
		| 'backgroundPositionY'
		| 'backgroundRepeat'
		| 'backgroundSize'
	>;
	readonly setValue: ( value?: ImageValue ) => void;
};

type ImageValue = {
	readonly url: string;
	readonly attachment: string;
	readonly position: string;
	readonly repeat: string;
	readonly size: string;
};

export function ColorPanelDropdown( {
	label,
	hasValue,
	onSelect,
	onDeselect,
	isShownByDefault,
	indicators,
	tabs,
	disabled,
}: ColorPanelDropdownProps ): JSX.Element {
	const colorGradientDropdownButtonRef =
		useRef< Maybe< HTMLElement > >( undefined );

	return (
		<ToolsPanelItem
			className="block-editor-tools-panel-color-gradient-settings__item"
			hasValue={ hasValue }
			label={ label }
			onSelect={ onSelect }
			onDeselect={ onDeselect }
			isShownByDefault={ isShownByDefault }
		>
			<Dropdown
				popoverProps={ {
					placement: 'left-start',
					offset: 36,
					shift: true,
				} }
				className="block-editor-tools-panel-color-gradient-settings__dropdown"
				renderToggle={ ( { onToggle, isOpen } ) => {
					const toggleProps = {
						onClick: onToggle,
						className: clsx(
							'block-editor-panel-color-gradient-settings__dropdown',
							{ 'is-open': isOpen }
						),
						'aria-expanded': isOpen,
						disabled,
						ref: colorGradientDropdownButtonRef,
					};

					return (
						<Button { ...toggleProps } __next40pxDefaultSize>
							<LabeledColorIndicators
								indicators={ indicators }
								label={ label }
							/>
						</Button>
					);
				} }
				renderContent={ getContentRenderer( tabs ) }
			/>
		</ToolsPanelItem>
	);
}

// =======
// HELPERS
// =======

const getContentRenderer =
	( tabs: ColorPanelDropdownProps[ 'tabs' ] ) => () => {
		if ( tabs.length === 1 ) {
			const { key: _, ...tab } = tabs[ 0 ];
			return (
				<DropdownContentWrapper paddingSize="none">
					<div className="block-editor-panel-color-gradient-settings__dropdown-content">
						{ 'image' === tab.type ? (
							<ImagePanelTab { ...tab } />
						) : (
							<ColorPanelTab { ...tab } />
						) }
					</div>
				</DropdownContentWrapper>
			);
		}

		const currentTab = tabs.find( ( tab ) => tab.userValue !== undefined );
		return (
			<DropdownContentWrapper paddingSize="none">
				<div className="block-editor-panel-color-gradient-settings__dropdown-content">
					<Tabs defaultTabId={ currentTab?.key }>
						<Tabs.TabList>
							{ tabs.map( ( tab ) => (
								<Tabs.Tab key={ tab.key } tabId={ tab.key }>
									{ tab.label }
								</Tabs.Tab>
							) ) }
						</Tabs.TabList>

						{ tabs.map( ( { key, ...tab } ) => (
							<Tabs.TabPanel
								key={ key }
								tabId={ key }
								focusable={ false }
							>
								{ 'image' === tab.type ? (
									<ImagePanelTab { ...tab } />
								) : (
									<ColorPanelTab { ...tab } />
								) }
							</Tabs.TabPanel>
						) ) }
					</Tabs>
				</div>
			</DropdownContentWrapper>
		);
	};

// ============
// HELPER VIEWS
// ============

const LabeledColorIndicators = ( {
	indicators,
	label,
}: {
	readonly indicators: ReadonlyArray< string >;
	readonly label: string;
} ) => (
	<HStack justify="flex-start">
		<ZStack isLayered={ false } offset={ -8 }>
			{ indicators.map( ( indicator, index ) => (
				<Flex key={ index } expanded={ false }>
					<ColorIndicator colorValue={ indicator } />
				</Flex>
			) ) }
		</ZStack>
		<FlexItem className="block-editor-panel-color-gradient-settings__color-name">
			{ label }
		</FlexItem>
	</HStack>
);

const ColorPanelTab = ( {
	type,
	userValue,
	inheritedValue,
	setValue,
}: Omit< ColorPanelTabProps, 'key' > ) => {
	const settings = getEditorSettings();
	const colors = useColorsPerOrigin( settings );
	const gradients = useGradientsPerOrigin( settings );

	return (
		<ColorGradientControl
			colors={ colors }
			gradients={ gradients }
			enableAlpha
			__experimentalIsRenderedInSidebar
			colorValue={
				'color' === type ? userValue ?? inheritedValue : undefined
			}
			gradientValue={
				'gradient' === type ? userValue ?? inheritedValue : undefined
			}
			onColorChange={ 'color' === type ? setValue : undefined }
			onGradientChange={ 'gradient' === type ? setValue : undefined }
			clearable={ inheritedValue === userValue }
			headingLevel={ 3 }
		/>
	);
};

const ImagePanelTab = ( {
	userValue,
	inheritedValue,
	disabledProps,
	setValue,
}: Omit< ImagePanelTabProps, 'key' > ) => {
	const currentUserValue = {
		background: {
			backgroundImage: {
				title: '',
				url: userValue?.url || '',
			},
			backgroundAttachment: userValue?.attachment || '',
			backgroundPosition: userValue?.position || '50% 0',
			backgroundSize: userValue?.size || 'cover',
			backgroundRepeat: userValue?.repeat || '',
		},
	};

	const currentInheritedValue = inheritedValue
		? {
				background: {
					backgroundImage: {
						title: '',
						url: inheritedValue.url || '',
					},
					backgroundAttachment: inheritedValue?.attachment || '50% 0',
					backgroundPosition: inheritedValue?.position || '50% 0',
					backgroundSize: inheritedValue?.size || 'cover',
					backgroundRepeat: inheritedValue?.repeat || '',
				},
		  }
		: undefined;

	return (
		<BackgroundImageControl
			value={ currentUserValue }
			onChange={ ( value ) => {
				const url =
					'string' === typeof value.background?.backgroundImage
						? value.background?.backgroundImage
						: value.background?.backgroundImage?.url ?? '';
				const attachment =
					value.background?.backgroundAttachment ||
					currentUserValue.background.backgroundAttachment;
				const position =
					value.background?.backgroundPosition ||
					currentUserValue.background.backgroundPosition;
				const repeat =
					value.background?.backgroundRepeat ||
					currentUserValue.background.backgroundRepeat;
				const size =
					value.background?.backgroundSize ||
					currentUserValue.background.backgroundSize;

				setValue( {
					url,
					attachment,
					position,
					repeat,
					size,
				} );
			} }
			disabledProps={ disabledProps }
			settings={ currentUserValue }
			inheritedValue={ currentInheritedValue }
		/>
	);
};
